import { AnimatePresence, motion } from 'framer-motion';
import { useChatStore } from '../../store/useChatStore';
import { ConfirmationCard } from './ConfirmationCard';

/**
 * Compact floating approval popover that appears above the FAB
 * only in the `awaiting_approval` voice state.
 *
 * Mic is auto-recording while this overlay is visible (managed by useVoiceMode).
 * Speaking is always a revision — user taps FAB checkmark to send.
 * "Looks good" and "Cancel" buttons discard any in-progress recording.
 */
export function VoiceOverlay() {
  const voiceState = useChatStore((s) => s.voiceState);
  const lastVoiceResponse = useChatStore((s) => s.lastVoiceResponse);
  const isLoading = useChatStore((s) => s.isLoading);
  const dismissVoiceOverlay = useChatStore((s) => s.dismissVoiceOverlay);
  const confirmActions = useChatStore((s) => s.confirmActions);
  const setVoiceState = useChatStore((s) => s.setVoiceState);

  const isVisible = voiceState === 'awaiting_approval' && !!lastVoiceResponse;

  const handleConfirm = () => {
    if (lastVoiceResponse?.messageId) {
      confirmActions(lastVoiceResponse.messageId);
      setVoiceState('processing');  // Overlay hides, recording cleanup handled by useVoiceMode effect
    }
  };

  const handleCancel = () => {
    // Recording cleanup handled by useVoiceMode effect when leaving awaiting_approval
    dismissVoiceOverlay();
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop — subtle scrim + click to dismiss */}
          <motion.div
            key="voice-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-[997] bg-black/20"
            onClick={handleCancel}
          />

          {/* Floating popover */}
          <motion.div
            key="voice-popover"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="fixed bottom-[calc(env(safe-area-inset-bottom)+144px)] right-4 z-[999] w-80 max-w-[calc(100vw-2rem)] drop-shadow-xl"
          >
            {lastVoiceResponse?.pendingActions && lastVoiceResponse.pendingActions.length > 0 ? (
              <ConfirmationCard
                actions={lastVoiceResponse.pendingActions}
                onConfirm={handleConfirm}
                onReject={handleCancel}
                disabled={isLoading}
              />
            ) : (
              <div className="rounded-2xl bg-card border border-border p-4 text-center shadow-soft">
                <p className="text-sm text-muted-foreground">
                  No changes to confirm.
                </p>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="mt-2 min-h-[36px] px-3 text-sm font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-full"
                >
                  Dismiss
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
